import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  collection, query, where, orderBy, limit,
  onSnapshot, doc, updateDoc, serverTimestamp,
  type DocumentData,
} from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { db } from '@/lib/firebase'
import { useWorkspaceStore } from '@/store/workspace'

export interface Application {
  id: string
  name: string
  email: string
  phone?: string
  portfolio?: string
  position: string
  positionSlug: string
  positionType?: 'Job' | 'Internship'
  resumeFileName?: string
  resumeMarkdown: string
  resumeText?: string
  coverLetter?: string
  source: string
  status: 'new' | 'reviewed' | 'shortlisted' | 'interviewing' | 'offered' | 'hired' | 'rejected'
  aiScore?: number
  aiSummary?: string
  aiRedFlags?: string[]
  aiStrengths?: string[]
  aiBreakdown?: Record<string, number>
  aiQuestions?: string[]
  aiRecommendation?: string
  aiProcessed: boolean
  notes?: Array<{ text: string; authorId: string; createdAt: string }>
  appliedAt: string
  updatedAt: string
}

function docToApp(d: DocumentData & { id: string }): Application {
  const data = d.data ? d.data() : d
  return { id: d.id, ...data } as Application
}

/** Real-time listener for applications */
export function useApplications(statusFilter?: Application['status']) {
  const { workspaceId } = useWorkspaceStore()
  const [apps, setApps] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!workspaceId) return
    setLoading(true)

    const constraints = [
      orderBy('appliedAt', 'desc'),
      limit(100),
    ]
    if (statusFilter) constraints.unshift(where('status', '==', statusFilter) as never)

    const q = query(
      collection(db, `workspaces/${workspaceId}/applications`),
      ...constraints
    )

    const unsub = onSnapshot(q, (snap) => {
      setApps(snap.docs.map(d => docToApp({ id: d.id, ...d.data() })))
      setLoading(false)
    })

    return unsub
  }, [workspaceId, statusFilter])

  return { apps, loading }
}

/** Single application */
export function useApplication(appId: string) {
  const { workspaceId } = useWorkspaceStore()

  return useQuery({
    queryKey: ['application', workspaceId, appId],
    enabled: !!workspaceId && !!appId,
    queryFn: async () => {
      const { getDoc } = await import('firebase/firestore')
      const snap = await getDoc(doc(db, `workspaces/${workspaceId}/applications/${appId}`))
      if (!snap.exists()) throw new Error('Application not found')
      return docToApp({ id: snap.id, ...snap.data() })
    },
  })
}

/** Update application status */
export function useUpdateStatus() {
  const { workspaceId } = useWorkspaceStore()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ appId, status }: { appId: string; status: Application['status'] }) => {
      await updateDoc(
        doc(db, `workspaces/${workspaceId}/applications/${appId}`),
        { status, updatedAt: serverTimestamp() }
      )
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['application'] }),
  })
}
